// Copyright (c) 2020 WiseTime. All rights reserved.

import { combineEpics } from "redux-observable"
import getTimelineData from "./getTimelineData"
import createEmptyTimeGroup from "./createEmptyTimeGroup"
import createTimeGroup from "./createTimeGroup"
import mergeTimeGroups from "./mergeTimeGroups"
import postTimeGroup from "./postTimeGroup"
import deleteTimeGroups from "./deleteTimeGroups"
import updateTimeGroup from "./updateTimeGroup"
import trashTimeRows from "./trashTimeRows"
import untrashTimeRows from "./untrashTimeRows"
import snackBar from "../../snackbar/epics/snackBar"
import watchTimelineUpdates from "./watchTimelineUpdates"
import listDaySummaries from "./listDaySummaries"
import updateTimeGroupDuration from "./updateTimeGroupDuration"
import attachTag from "./attachTag"
import replaceTag from "./replaceTag"
import setActivityType from "./setActivityType"
import unsetActivityType from "./unsetActivityType"
import detachTag from "./detachTag"
import removeFromTimeGroup from "./removeFromTimeGroup"
import declineTagSuggestion from "./declineTagSuggestion"
import detachTagCategory from "./detachTagCategory"
import emptyTrash from "./emptyTrash"
import addToTimeGroup from "./addToTimeGroup"
import postTime from "./postTime"
import cancelPostTime from "./cancelPostTime"
import listTimeRowsByDate from "./listTimeRowsByDate"
import listTimesheetFields from "./listTimesheetFields"
import listFavouriteTimesheetFieldOptions from "./listFavouriteTimesheetFieldOptions"
import listRecentTimesheetFieldOptions from "./listRecentTimesheetFieldOptions"
import addFavouriteTimesheetFieldOption from "./addFavouriteTimesheetFieldOption"
import removeFavouriteTimesheetFieldOption from "./removeFavouriteTimesheetFieldOption"
import reorderFavouriteTimesheetFieldOption from "./reorderFavouriteTimesheetFieldOption"
import generateTimesheetFieldConfigs from "./generateTimesheetFieldConfigs"
import batchGetTimesheetFieldConfigs from "./batchGetTimesheetFieldConfigs"
import refreshTimesheetFieldConfigs from "./refreshTimesheetFieldConfigs"
import batchGetTimesheetFieldOptions from "./batchGetTimesheetFieldOptions"
import selectTimesheetFieldOption from "./selectTimesheetFieldOption"
import clearTimesheetField from "./clearTimesheetField"
import selectSuggestedTimesheetFieldOption from "./syncTimesheetFieldSelections"
import immediatelyDeleteTimeRows from "./immediatelyDeleteTimeRows"
import onTimelineOutOfSync from "./onTimelineOutOfSync"
import clearTimeGroupErrors from "./clearTimeGroupErrors"
import loadWarnMultipleTags from "./loadWarnMultipleTags"
import persistWarnMultipleTags from "./persistWarnMultipleTags"
import loadActionPaneNoteIsHidden from "./loadActionPaneNoteIsHidden"
import persistActionPaneNoteIsHidden from "./persistActionPaneNoteIsHidden"
import loadAutoNarrative from "./loadAutoNarrative"
import persistAutoNarrative from "./persistAutoNarrative"
import loadTilesViewType from "./loadTilesViewType"
import persistTilesViewType from "./persistTilesViewType"
import loadTagsViewType from "./loadTagsViewType"
import persistTagsViewType from "./persistTagsViewType"
import loadTimesheetFieldsViewType from "./loadTimesheetFieldsViewType"
import persistTimesheetFieldsViewType from "./persistTimesheetFieldsViewType"
import loadTimesheetFieldViewMode from "./loadTimesheetFieldViewMode"
import persistTimesheetFieldViewMode from "./persistTimesheetFieldViewMode"
import loadTimeGroupsDisplayMode from "./loadTimeGroupsDisplayMode"
import persistTimeGroupsDisplayMode from "./persistTimeGroupsDisplayMode"
import listLeaves from "./listLeaves"
import createLeave from "./createLeave"
import deleteLeave from "./deleteLeave"
import listMemberExpectations from "./listMemberExpectations"
import refreshMemberExpectations from "./refreshMemberExpectations"
import { storagePrefix } from "../../../app/config"
import loadEmptyTrashDoNotAskAgain from "./loadEmptyTrashDoNotAskAgain"
import persistEmptyTrashDoNotAskAgain from "./persistEmptyTrashDoNotAskAgain"
import generateNarrative from "./generateNarrative"
import translateNarratives from "./translateNarratives"
import syncCurrentDay from "./syncCurrentDay"
import deleteTimelineMarker from "./deleteTimelineMarker"
import checkPendingPostStatus from "./checkPendingPostStatus"
import periodicRefreshTimesheetFieldConfigs from "./periodicRefreshTimesheetFieldConfigs"

// Important: changing WARN_MULTIPLE_TAGS value requires sync with wt-customer-frontend where it's reused in the internal logic
export const WARN_MULTIPLE_TAGS = storagePrefix + "warn_multiple_tags"
export const ACTION_PANE_NOTE_IS_HIDDEN = storagePrefix + "action_pane_note_is_hidden"
export const TILES_VIEW_TYPE = storagePrefix + "tiles_view_type"
export const GRID_TAGS_VIEW_TYPE = storagePrefix + "grid_tags_view_type"
export const TIMESHEET_FIELDS_VIEW_TYPE = storagePrefix + "timesheet_fields_view_type"
export const TIME_GROUPS_DISPLAY_MODE = storagePrefix + "time_groups_display_mode"
export const EMPTY_TRASH_DO_NOT_ASK_AGAIN_KEY = storagePrefix + "empty_trash_do_not_ask_again"
export const AUTO_NARRATIVE = storagePrefix + "auto_narrative"
export const TIMESHEET_FIELD_VIEW_MODE = storagePrefix + "timesheet_field_view_mode"

export default combineEpics(
  getTimelineData,
  listDaySummaries,
  onTimelineOutOfSync,

  createEmptyTimeGroup,
  createTimeGroup,
  postTimeGroup,
  addToTimeGroup,
  updateTimeGroup,
  updateTimeGroupDuration,
  mergeTimeGroups,
  deleteTimeGroups,
  attachTag,
  replaceTag,
  setActivityType,
  unsetActivityType,
  detachTag,
  declineTagSuggestion,
  detachTagCategory,

  emptyTrash,
  trashTimeRows,
  untrashTimeRows,
  immediatelyDeleteTimeRows,

  snackBar,
  watchTimelineUpdates,
  removeFromTimeGroup,

  postTime,
  cancelPostTime,
  listTimeRowsByDate,

  listTimesheetFields,
  listFavouriteTimesheetFieldOptions,
  listRecentTimesheetFieldOptions,
  addFavouriteTimesheetFieldOption,
  removeFavouriteTimesheetFieldOption,
  reorderFavouriteTimesheetFieldOption,
  generateTimesheetFieldConfigs,
  batchGetTimesheetFieldConfigs,
  refreshTimesheetFieldConfigs,
  batchGetTimesheetFieldOptions,
  selectTimesheetFieldOption,
  clearTimesheetField,
  selectSuggestedTimesheetFieldOption,

  clearTimeGroupErrors,

  loadWarnMultipleTags,
  persistWarnMultipleTags,

  loadActionPaneNoteIsHidden,
  persistActionPaneNoteIsHidden,

  loadAutoNarrative,
  persistAutoNarrative,

  loadTilesViewType,
  persistTilesViewType,
  
  loadTagsViewType,
  persistTagsViewType,

  loadTimesheetFieldsViewType,
  persistTimesheetFieldsViewType,

  loadTimesheetFieldViewMode,
  persistTimesheetFieldViewMode,

  loadTimeGroupsDisplayMode,
  persistTimeGroupsDisplayMode,

  listLeaves,
  createLeave,
  deleteLeave,

  listMemberExpectations,
  refreshMemberExpectations,

  loadEmptyTrashDoNotAskAgain,
  persistEmptyTrashDoNotAskAgain,

  generateNarrative,
  translateNarratives,
  syncCurrentDay,
  deleteTimelineMarker,

  checkPendingPostStatus,
  periodicRefreshTimesheetFieldConfigs,
)
